import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Bot, ClipboardList, Lightbulb, MessageSquareText, Send, Wrench, X } from 'lucide-react';
import { AdminAPI } from '@shared/types/api';
import {
  useAdminAgentRunDetail,
  useCancelAgentRun,
  useResumeAgentRun,
} from '../../api/useAdminOps';
import CollapsibleMarkdown from './CollapsibleMarkdown';
import DecisionCard from './DecisionCard';
import { RUN_STATUS_STYLES, RefreshButton, StatusChip, TASK_LABELS, timeAgo } from './opsUi';

// Full run detail: header (task, trigger, status, cost) → chronological
// narrative stream (agent_run_events) interleaved with audited actions
// (events_audit_logs via agent_run_id) → final summary → raised decisions.

interface StreamItem {
  at: string;
  kind: 'narrative' | 'action';
  event?: AdminAPI.AgentRunEvent;
  audit?: AdminAPI.AuditLogEntry;
}

const EVENT_ICONS: Record<string, typeof Lightbulb> = {
  decision: Lightbulb,
  observation: MessageSquareText,
  progress: ClipboardList,
};

function describeAudit(entry: AdminAPI.AuditLogEntry): string {
  const meta = (entry.metadata ?? {}) as Record<string, unknown>;
  switch (entry.action) {
    case 'assignment.create':
      return `Assigned booking ${entry.bookingId} to table ${entry.availableTableId}`;
    case 'assignment.move':
      return `Moved booking ${entry.bookingId}: ${meta.fromTableId} → ${meta.toTableId}`;
    case 'assignment.remove':
      return `Unassigned booking ${entry.bookingId} from table ${meta.fromTableId ?? entry.availableTableId}`;
    case 'table.create':
      return `Created table ${entry.availableTableId ?? String(meta.restaurantName ?? '')}`;
    case 'table.update':
      return `Updated table ${entry.availableTableId}`;
    case 'table.delete':
      return `Deleted table ${entry.availableTableId}`;
    default:
      return `${entry.action}${entry.availableTableId ? ` (table ${entry.availableTableId})` : ''}`;
  }
}

export default function RunDetailDrawer({
  runId,
  onClose,
}: {
  runId: string | null;
  onClose: () => void;
}) {
  const { data, isLoading, isFetching, refetch } = useAdminAgentRunDetail(runId);
  const cancelRun = useCancelAgentRun();
  const resumeRun = useResumeAgentRun();
  const [instruction, setInstruction] = useState('');
  const detail = data?.data;

  const stream: StreamItem[] = detail
    ? [
        ...detail.events.map((e) => ({ at: e.createdAt, kind: 'narrative' as const, event: e })),
        ...detail.auditEntries.map((a) => ({ at: a.createdAt, kind: 'action' as const, audit: a })),
      ].sort((a, b) => a.at.localeCompare(b.at))
    : [];

  const metrics = (detail?.run.metrics ?? {}) as Record<string, unknown>;
  const isActive = detail?.run.status === 'running' || detail?.run.status === 'queued';

  return (
    <AnimatePresence>
      {runId && (
        <>
          <motion.div
            className="fixed inset-0 bg-black/50 z-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            className="fixed right-0 top-0 bottom-0 w-full max-w-2xl bg-white dark:bg-neutral-900 z-50 shadow-2xl overflow-y-auto"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 300, damping: 32 }}
          >
            <div className="sticky top-0 bg-white dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-700 p-4 flex items-start justify-between gap-3 z-10">
              <div className="flex items-center gap-2.5 min-w-0">
                <Bot className="w-5 h-5 text-primary-500 shrink-0" />
                <div className="min-w-0">
                  <h3 className="font-semibold text-sm truncate">
                    {detail ? (TASK_LABELS[detail.run.taskType] ?? detail.run.taskType) : 'Run'}
                    {detail?.run.neighborhoodName
                      ? ` — ${detail.run.neighborhoodName}`
                      : detail?.run.neighborhoodId === null
                        ? ' — Event-wide'
                        : ''}
                  </h3>
                  {detail && (
                    <div className="flex items-center gap-2 mt-0.5 text-xs text-dark-muted flex-wrap">
                      <StatusChip status={detail.run.status} styles={RUN_STATUS_STYLES} />
                      <span>trigger: {detail.run.trigger}</span>
                      <span>attempt {detail.run.attempt}</span>
                      <span>{timeAgo(detail.run.createdAt)}</span>
                      {typeof metrics.costUsd === 'number' && <span>${(metrics.costUsd as number).toFixed(2)}</span>}
                      {detail.run.rerunRequested && (
                        <span className="text-amber-600 font-medium">rerun requested</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <RefreshButton onClick={() => refetch()} isFetching={isFetching} />
                {isActive && (
                  <button
                    onClick={() => detail && cancelRun.mutate(detail.run.id)}
                    className="px-2.5 py-1 rounded-lg border border-red-300 text-red-600 text-xs font-medium hover:bg-red-50 dark:hover:bg-red-900/20"
                  >
                    Cancel run
                  </button>
                )}
                <button onClick={onClose} className="p-1.5 text-dark-muted hover:text-primary-500">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-4 space-y-5">
              {isLoading && <p className="text-sm text-dark-muted">Loading…</p>}

              {detail?.run.error && (
                <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-3 text-sm text-red-700 dark:text-red-300">
                  {detail.run.error}
                </div>
              )}

              {stream.length > 0 && (
                <section>
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-dark-muted mb-2">
                    Decision & action stream
                  </h4>
                  <ol className="space-y-2.5 border-l-2 border-neutral-200 dark:border-neutral-700 pl-4">
                    {stream.map((item, i) => {
                      if (item.kind === 'action' && item.audit) {
                        return (
                          <li key={`a-${item.audit.id ?? i}`} className="relative">
                            <Wrench className="w-3.5 h-3.5 absolute -left-[23px] top-0.5 bg-white dark:bg-neutral-900 text-primary-500" />
                            <p className="text-sm">{describeAudit(item.audit)}</p>
                            <p className="text-[11px] text-dark-muted">{timeAgo(item.at)}</p>
                          </li>
                        );
                      }
                      const event = item.event!;
                      const Icon = EVENT_ICONS[event.type] ?? ClipboardList;
                      return (
                        <li key={`e-${event.id}`} className="relative">
                          <Icon className="w-3.5 h-3.5 absolute -left-[23px] top-0.5 bg-white dark:bg-neutral-900 text-amber-500" />
                          <p className="text-sm font-medium">{event.title}</p>
                          {event.detail && (
                            <CollapsibleMarkdown
                              className="text-dark-muted break-words mt-0.5"
                              collapseAt={260}
                            >
                              {event.detail}
                            </CollapsibleMarkdown>
                          )}
                          <p className="text-[11px] text-dark-muted mt-0.5">
                            {event.type} · {timeAgo(event.createdAt)}
                          </p>
                        </li>
                      );
                    })}
                  </ol>
                </section>
              )}

              {detail?.run.summary && (
                <section>
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-dark-muted mb-2">
                    Final report
                  </h4>
                  <div className="rounded-lg bg-neutral-50 dark:bg-neutral-800 p-3">
                    <CollapsibleMarkdown className="break-words" collapseAt={600}>
                      {detail.run.summary}
                    </CollapsibleMarkdown>
                  </div>
                </section>
              )}

              {detail && detail.decisions.length > 0 && (
                <section>
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-dark-muted mb-2">
                    Decisions raised by this run
                  </h4>
                  <div className="space-y-2">
                    {detail.decisions.map((d) => (
                      <DecisionCard key={d.id} decision={d} />
                    ))}
                  </div>
                </section>
              )}

              {detail && !isActive && (
                <section>
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-dark-muted mb-2">
                    Resume with follow-up instruction
                  </h4>
                  <p className="text-xs text-dark-muted mb-2">
                    Queues a new run for this scope that continues this session with your
                    instruction (full prior context when available).
                  </p>
                  <div className="flex gap-2">
                    <textarea
                      value={instruction}
                      onChange={(e) => setInstruction(e.target.value)}
                      placeholder="e.g. Swap Alex and Sam anyway — the age gap concern doesn't apply here…"
                      rows={2}
                      className="flex-1 px-3 py-2 rounded-lg border border-neutral-300 dark:border-neutral-600 bg-transparent text-sm resize-y"
                    />
                    <button
                      onClick={() =>
                        resumeRun.mutate(
                          { runId: detail.run.id, instruction: instruction.trim() },
                          { onSuccess: () => setInstruction('') }
                        )
                      }
                      disabled={!instruction.trim() || resumeRun.isPending}
                      className="self-start inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-primary-500 text-white text-xs font-medium hover:bg-primary-600 disabled:opacity-50"
                    >
                      <Send className="w-3.5 h-3.5" />
                      {resumeRun.isPending ? 'Queuing…' : 'Resume'}
                    </button>
                  </div>
                  {resumeRun.isSuccess && (
                    <p className="mt-1.5 text-xs text-emerald-600">
                      {resumeRun.data?.message ?? 'Follow-up run queued.'}
                    </p>
                  )}
                  {resumeRun.isError && (
                    <p className="mt-1.5 text-xs text-red-600">
                      Failed: {(resumeRun.error as Error)?.message ?? 'unknown error'}
                    </p>
                  )}
                </section>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
